export type IsrTabId = "isr" | "salesConfig" | "salesGoal";

export type IsrRecord = {
  isrId: number;
  fullName: string;
  email: string;
  active: boolean;
};

export type IsrUserEmail = {
  userId: number;
  email: string;
  fullName: string;
};

export type SalesGoalRecord = {
  salesGoalId: number;
  year: string;
  month: string;
  invoiceYear: string;
  collectionTarget: string;
  actualCollectedAmt: string;
  achievementPct: string;
};

export type SalesConfigRateType = "STANDARD" | "TARGET_BASED";

export const SALES_CONFIG_TARGET_RANGE_MAX_LENGTH = 50;

export type SalesConfigTargetRateRecord = {
  targetRange: string;
  targetCompPercent: string;
  active?: boolean;
};

export type SalesConfigTargetRateFormRow = {
  targetRange: string;
  targetCompPercent: string;
  active: boolean;
};

export type SalesConfigTargetRatesFormValues = {
  targetRates: SalesConfigTargetRateFormRow[];
};

const SALES_CONFIG_TARGET_RANGE_PLUS_MAX = 999.99;

export type ParsedSalesConfigTargetRange = {
  from: number;
  to: number;
};

// CS-2144 START
export type SalesConfigTargetRangeEditorValues = {
  targetPercentFrom: string;
  targetPercentTo: string;
  noUpperLimit: boolean;
};

export const parseSalesConfigTargetRange = (value: string | null | undefined): ParsedSalesConfigTargetRange | null => {
  if (!value || !String(value).trim()) {
    return null;
  }
  const trimmed = String(value).trim().replace(/\u2013/g, "-");

  const plusMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*%?\s*\+$/i);
  if (plusMatch) {
    const from = Number(plusMatch[1]);
    if (Number.isNaN(from) || from < 0) {
      return null;
    }
    return { from, to: SALES_CONFIG_TARGET_RANGE_PLUS_MAX };
  }

  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*%?\s*-\s*(\d+(?:\.\d+)?)\s*%?$/i);
  if (rangeMatch) {
    const from = Number(rangeMatch[1]);
    const to = Number(rangeMatch[2]);
    if (Number.isNaN(from) || Number.isNaN(to) || from >= to) {
      return null;
    }
    return { from, to };
  }

  return null;
};

export const getSalesConfigTargetRangeEditorValues = (
  value: string | null | undefined
): SalesConfigTargetRangeEditorValues => {
  const parsed = parseSalesConfigTargetRange(value);
  if (!parsed) {
    return { targetPercentFrom: "", targetPercentTo: "", noUpperLimit: false };
  }
  const noUpperLimit = String(value).trim().endsWith("+");
  return {
    targetPercentFrom: String(parsed.from),
    targetPercentTo: noUpperLimit ? "" : String(parsed.to),
    noUpperLimit,
  };
};

export const formatSalesConfigTargetRange = (
  from: string | number,
  to: string | number,
  noUpperLimit: boolean
) => noUpperLimit
  ? `${Number(from)}%+`
  : `${Number(from)}-${Number(to)}%`;
// CS-2144 END

export const salesConfigTargetRangesOverlap = (
  fromA: number,
  toA: number,
  fromB: number,
  toB: number
) => fromA <= toB && fromB <= toA;

export const validateSalesConfigTargetRange = (value: string | null | undefined) => {
  if (!value || !String(value).trim()) {
    return "Target Range is required.";
  }
  if (String(value).trim().length > SALES_CONFIG_TARGET_RANGE_MAX_LENGTH) {
    return `Target Range cannot exceed ${SALES_CONFIG_TARGET_RANGE_MAX_LENGTH} characters.`;
  }
  if (!parseSalesConfigTargetRange(value)) {
    return "Enter a valid range such as 50-99% or 100%+.";
  }
  return true;
};

export const validateSalesConfigTargetRangeOverlap = (
  value: string | null | undefined,
  existingRanges: string[],
  excludeIndex?: number | null
) => {
  const parsed = parseSalesConfigTargetRange(value);
  if (!parsed) {
    return true;
  }
  for (let index = 0; index < existingRanges.length; index += 1) {
    if (excludeIndex != null && index === excludeIndex) {
      continue;
    }
    const other = parseSalesConfigTargetRange(existingRanges[index]);
    if (!other) {
      continue;
    }
    if (salesConfigTargetRangesOverlap(parsed.from, parsed.to, other.from, other.to)) {
      return `This range overlaps an existing entry (${existingRanges[index]}).`;
    }
  }
  return true;
};

export const validateSalesConfigTargetRangeListNoOverlap = (ranges: string[]) => {
  const parsed = ranges.map((raw) => ({ raw, bounds: parseSalesConfigTargetRange(raw) }));
  for (let i = 0; i < parsed.length; i += 1) {
    if (!parsed[i].bounds) {
      continue;
    }
    for (let j = i + 1; j < parsed.length; j += 1) {
      if (!parsed[j].bounds) {
        continue;
      }
      const a = parsed[i].bounds!;
      const b = parsed[j].bounds!;
      if (salesConfigTargetRangesOverlap(a.from, a.to, b.from, b.to)) {
        return `Ranges "${parsed[i].raw}" and "${parsed[j].raw}" overlap.`;
      }
    }
  }
  return true;
};

export type SalesConfigRecord = {
  salesConfigId: number;
  isrId: number;
  isrName?: string;
  invoiceYear: string;
  effectiveFrom: string;
  effectiveTo: string;
  alignment: string;
  scope: string;
  rateType: SalesConfigRateType;
  standardCompPercent: string;
  targetRates: SalesConfigTargetRateRecord[];
};

export type IsrFormValues = {
  userId?: number;
  agencyName: string;
  email: string;
  phone: string;
  streetAddress: string;
  unitNumber: string;
  zipcode: string;
  city: string;
  state: string;
  commissionType: string;
  active: boolean;
};

export type SalesGoalFormValues = {
  year: string;
  month: string;
  invoiceYear: string;
  collectionTarget: string;
  actualCollectedAmt: string;
  achievementPct: string;
};

export type SalesConfigFormValues = {
  isrId: string;
  invoiceYear: string;
  effectiveFrom: any;
  effectiveTo: any;
  alignment: string;
  scope: string;
  rateType: SalesConfigRateType;
  standardCompPercent: string;
};

export const MONTH_OPTIONS = [
  { label: "Jan", value: "Jan" },
  { label: "Feb", value: "Feb" },
  { label: "Mar", value: "Mar" },
  { label: "Apr", value: "Apr" },
  { label: "May", value: "May" },
  { label: "Jun", value: "Jun" },
  { label: "Jul", value: "Jul" },
  { label: "Aug", value: "Aug" },
  { label: "Sep", value: "Sep" },
  { label: "Oct", value: "Oct" },
  { label: "Nov", value: "Nov" },
  { label: "Dec", value: "Dec" },
];

export const SALES_GOAL_YEAR_MIN = 2000;
export const SALES_GOAL_YEAR_MAX = 2100;
export const SALES_GOAL_COLLECTION_TARGET_MAX = 999999999.99;

export const validateSalesGoalYear = (value: string, label = "Year") => {
  if (!value) return `${label} is required.`;
  if (!/^\d{4}$/.test(value)) return `Enter a valid 4-digit ${label.toLowerCase()}.`;
  const year = Number(value);
  if (year < SALES_GOAL_YEAR_MIN || year > SALES_GOAL_YEAR_MAX) {
    return `${label} must be between ${SALES_GOAL_YEAR_MIN} and ${SALES_GOAL_YEAR_MAX}.`;
  }
  return true;
};

export const validateCollectionTarget = (value: string | number | null | undefined) => {
  if (value === "" || value === null || value === undefined) {
    return "Collection Target is required.";
  }
  const amount = Number(value);
  if (Number.isNaN(amount) || amount <= 0) {
    return "Collection Target must be greater than 0.";
  }
  if (amount > SALES_GOAL_COLLECTION_TARGET_MAX) {
    return "Collection Target cannot exceed $999,999,999.99.";
  }
  return true;
};

export const isCollectionTargetAllowed = (values: { floatValue?: number }) => {
  return values.floatValue === undefined || values.floatValue <= SALES_GOAL_COLLECTION_TARGET_MAX;
};

export const validateOptionalActualCollectedAmt = (value: string | number | null | undefined) => {
  if (value === "" || value === null || value === undefined) {
    return true;
  }
  const amount = Number(value);
  if (Number.isNaN(amount) || amount < 0) {
    return "Actual Collected Amount must be 0 or greater.";
  }
  if (amount > SALES_GOAL_COLLECTION_TARGET_MAX) {
    return "Actual Collected Amount cannot exceed $999,999,999.99.";
  }
  return true;
};

export const isActualCollectedAmtAllowed = (values: { floatValue?: number }) => {
  return values.floatValue === undefined || values.floatValue <= SALES_GOAL_COLLECTION_TARGET_MAX;
};

export const validateOptionalAchievementPct = (value: string | number | null | undefined) => {
  if (value === "" || value === null || value === undefined) {
    return true;
  }
  return validateTargetPercentBound(value, "Achievement %", { allowZero: true });
};

/** Percent fields: 0–100 with at most two decimal places (matches DECIMAL(5,2) on sales_config). */
export const TARGET_PERCENT_MAX = 999.99;

export const validateTargetPercentBound = (
  value: string | number | null | undefined,
  label: string,
  options: { allowZero?: boolean } = {}
) => {
  if (value === "" || value === null || value === undefined) {
    return `${label} is required.`;
  }
  const raw = String(value).trim();
  const num = Number(raw);
  if (Number.isNaN(num) || !Number.isFinite(num)) {
    return `${label} must be a valid number.`;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
    return `${label} must be a number with at most 2 decimal places.`;
  }
  const min = options.allowZero ? 0 : 0.01;
  if (num < min || num > TARGET_PERCENT_MAX) {
    return options.allowZero
      ? `${label} must be between 0 and ${TARGET_PERCENT_MAX}.`
      : `${label} must be greater than 0 and cannot exceed ${TARGET_PERCENT_MAX}.`;
  }
  return true;
};

// CS-2144 START
export const validateSalesConfigTargetRangeBounds = (
  from: string | number | null | undefined,
  to: string | number | null | undefined,
  noUpperLimit: boolean
) => {
  const fromValidation = validateTargetPercentBound(from, "From %", { allowZero: true });
  if (fromValidation !== true) {
    return fromValidation;
  }
  if (noUpperLimit) {
    return true;
  }
  const toValidation = validateTargetPercentBound(to, "To %", { allowZero: true });
  if (toValidation !== true) {
    return toValidation;
  }
  if (Number(to) <= Number(from)) {
    return "To % must be greater than From %.";
  }
  return true;
};
// CS-2144 END

export const validateOptionalCompPercent = (value: string | number | null | undefined, label = "Percent") => {
  if (value === "" || value === null || value === undefined) {
    return true;
  }
  return validateCompPercent(value, label);
};

export const validateCompPercent = (value: string | number | null | undefined, label = "Percent") => {
  if (value === "" || value === null || value === undefined) {
    return `${label} is required.`;
  }
  const raw = String(value).trim();
  const num = Number(raw);
  if (Number.isNaN(num) || !Number.isFinite(num)) {
    return `${label} must be a valid number.`;
  }
  if (num <= 0 || num > 100) {
    return `${label} must be greater than 0 and cannot exceed 100.`;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
    return `${label} must be a number with at most 2 decimal places.`;
  }
  return true;
};

export const getIsrSaveErrorMessage = (actionResult: any, fallback: string): string => {
  if (actionResult?.meta?.requestStatus === "rejected") {
    const payload = actionResult.payload;
    if (payload && typeof payload === "object" && payload.message) {
      return String(payload.message);
    }
  }
  if (actionResult?.payload?.message) {
    return String(actionResult.payload.message);
  }
  return fallback;
};

export const ALIGNMENT_OPTIONS = [
  { label: "Direct", value: "Direct" },
  { label: "Indirect", value: "Indirect" },
];

export const SCOPE_OPTIONS = [
  { label: "All Invoices", value: "All Invoices" },
];

export const RATE_TYPE_OPTIONS = [
  { value: "STANDARD", label: "Standard Rate" },
  { value: "TARGET_BASED", label: "Target Based Rate" },
];
